import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { translationPropType } from '@eventbrite/i18n';
import { getAdditionalProps } from '@eventbrite/eds-utils';

import './objectFitImage.scss';

/**
 * The CSS property `object-fit: contain` isn't supported in IE at all.
 * But all target browsers support `background-size: contain`, which
 * behaves in the same way but only for background images.
 * This cross-browser polyfill uses an actual image tag AND a
 * background image.  The actual image tag is used for automatic
 * sizing of the container, so has its visibility set to hidden.  Sighted
 * user will actually be seeing a background-image applied to the
 * parent container so we can take advantage of `background-size: contain`.
 * Non-sighted users will still have the <img /> and alt attribute in
 * normal document flow.
 * This methodology tested in IE10 & IE11.
 */

export default class ObjectFitImage extends PureComponent {
    static propTypes = {
        /**
         * Source of the image to be displayed
         */
        imageSrc: PropTypes.string.isRequired,

        /**
         * Alt attribute of the actual image tag
         */
        imageAlt: translationPropType.isRequired,

        /**
         * Any additional className to be applied to the image tag.
         * This should be used to set max-height and min-height.
         */
        imageClassName: PropTypes.string,

        /**
         * Any additional className to be applied to the container.
         */
        containerClassName: PropTypes.string,
    };

    render() {
        const { imageSrc, imageAlt, imageClassName, containerClassName } =
            this.props;
        const containerClasses = classNames(
            'object-fit-image__container',
            containerClassName,
        );
        const containerStyle = {
            backgroundImage: `url(${imageSrc})`,
        };
        const imageClasses = classNames(
            'object-fit-image__image',
            'eds-max-img',
            imageClassName,
        );
        const extraAttrs = getAdditionalProps(this);

        return (
            <div className={containerClasses} style={containerStyle}>
                <img
                    className={imageClasses}
                    alt={imageAlt}
                    src={imageSrc}
                    {...extraAttrs}
                />
            </div>
        );
    }
}
